import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import posed from 'react-pose';
import { Translate, withLocalize } from 'react-localize-redux';
import classNames from 'classnames';
import css from './FieldGroup.css';
import CheckableFieldCSS from './CheckableField.css';
import CheckableField from './CheckableField';
import Dropdown from './Dropdown';
import TagInput from '../widgets/TagInput';
import Tooltip from './Tooltip';
import AutoComplete from './AutoComplete';
import Button from '../atoms/Button';
import WarningMessage from '../utilities/WarningMessage';
import { reportTypes } from '../../utils/reports';
import NewTagInput from '../widgets/NewTagInput';
import { REPORT_QUERY_PREFIX, trackMixpanelEvent } from '../../utils/general';
import Icon, { IconSizes } from '../foundations/Icon';
import Tag from '../atoms/Tag';
import { showFieldsAvailabilityForField } from '../../fieldsAvailability/utils';

const AnimationItem = posed.div({
    pressable: true,
    init: {
        opacity: 1,
    },
    press: {
        applyAtEnd: { opacity: 1 },
        applyAtStart: { opacity: 0.5 },
        opacity: 1,
        transition: {
            opacity: { ease: 'easeInOut', duration: 400 },
            default: { ease: 'linear', duration: 400 },
        },
    },
});

function FieldGroup({
    fields = [],
    onFieldClick = () => {},
    addButton = {
        show: false,
        text: '',
        onClick: () => {},
    },
    group = { type: 'multi', data: {} },
    reportType = 'reports',
    style = {},
    onFieldsAvailabilityClick = () => {},
    onDownloadReport = () => {},
    showFieldsAvailabilityFeature = false,
    children = null,
    selectedSources = [],
    dataTypeFields = [],
    labelTooltip,
    label = '',
    translate = () => {},
    warnings = [],
    showDownloadReport = false,
    labelPosition = 'left',
    labelStyle = {},
    containerStyle = {},
    renderChildrenOnly = false,
    fieldGroupWarning = '',
    fieldGroupWarningParameters = {},
    fieldGroupError,
    fieldGroupErrorParameters,
    bottomComponent = null,
    icon,
}) {
    const getTags = useCallback(() => {
        return fields
            .filter(field => field.checked)
            .map(field => ({
                text: field.display_name,
                id: field.name,
            }));
    }, [fields]);

    const getSuggestions = useCallback(() => {
        return fields.map(field => field.display_name);
    }, [fields]);

    const onTagChange = useCallback(
        tag => {
            const foundField = fields.find(field => field.display_name === tag);
            onFieldClick(foundField);
        },
        [fields, onFieldClick]
    );

    useEffect(() => {
        showDownloadReport && trackMixpanelEvent(REPORT_QUERY_PREFIX, 'Shown Download Report');
    }, [showDownloadReport]);

    const getFieldKey = field => {
        return `${field.name}_${field.id}`;
    };

    const shouldShowFieldAsDisabled = field =>
        !field.only_admin_visible &&
        (!!(
            !field.checked &&
            selectedSources &&
            selectedSources.length &&
            field.available_sources &&
            !selectedSources.some(source => field.available_sources.includes(source))
        ) ||
            !!(
                !field.checked &&
                dataTypeFields?.length &&
                field.available_data_types?.length &&
                !dataTypeFields.some(type => field.available_data_types.includes(type))
            ));

    const getGroup = () => {
        const onFieldClickSingle = field => {
            onFieldClick(field, true);
        };

        let ret = null;

        if (group.type === 'multi' || group.type === 'single') {
            ret = (
                <div className={css.fieldsContainer} style={style}>
                    <div className={css.fields}>
                        {addButton.show ? (
                            <Button type="secondary" Icon="add-fill" className={css.addButton} {...addButton} />
                        ) : null}
                        {fields.map(field => (
                            <AnimationItem
                                key={`pose${getFieldKey(field)}`}
                                pose={field.checked ? 'press' : 'init'}
                                style={{ display: 'inline-block' }}
                            >
                                <CheckableField
                                    itemId={field.name}
                                    showInFieldsAvailability={actualType => {
                                        return showFieldsAvailabilityForField(
                                            showFieldsAvailabilityFeature,
                                            reportType,
                                            actualType
                                        );
                                    }}
                                    groupType={group.type}
                                    key={getFieldKey(field)}
                                    field={field}
                                    availableSources={field.available_sources}
                                    checkMarkOnSelected={field.ui_configuration?.checkmark_on_selected}
                                    checkedFieldClassName={
                                        CheckableFieldCSS[field.ui_configuration?.checked_field_class_name]
                                    }
                                    fieldClassName={CheckableFieldCSS[field.ui_configuration?.field_class_name]}
                                    dataTypeFields={dataTypeFields}
                                    shouldShowDisabled={shouldShowFieldAsDisabled(field)}
                                    {...field}
                                    onClick={group.type === 'single' ? onFieldClickSingle : onFieldClick}
                                    onFieldsAvailabilityClick={onFieldsAvailabilityClick}
                                    style={{
                                        ...((group.data && group.data.buttonStyle) || {}),
                                    }}
                                    hideIcon
                                    reportType={reportType}
                                />
                            </AnimationItem>
                        ))}
                    </div>
                    <div>{children}</div>
                </div>
            );
        }

        if (group.type === 'dropdown') {
            ret = (
                <Dropdown
                    disabled={group.disabled}
                    items={fields}
                    onSelection={field => {
                        onFieldClick(field);
                    }}
                    {...group.data}
                />
            );
        }

        if (group.type === 'autocomplete') {
            ret = (
                <AutoComplete
                    onChange={val => {
                        onFieldClick(val);
                    }}
                    {...group.data}
                />
            );
        }

        if (group.type === 'creatable') {
            ret = (
                <AutoComplete
                    isCreatable
                    onChange={option => {
                        let val = '';
                        if (option && option.value) {
                            val = option.value;
                        }
                        onFieldClick(val);
                    }}
                    {...group.data}
                />
            );
        }

        if (group.type === 'tag') {
            ret = (
                <div className={css.tagContainer}>
                    <TagInput
                        containerStyle={{
                            width: '100%',
                            height: 'auto',
                        }}
                        tags={getTags()}
                        disabled={false}
                        suggestions={getSuggestions()}
                        onTagAdded={onTagChange}
                        onTagRemoved={onTagChange}
                        {...group.data}
                    />
                </div>
            );
        }

        if (group.type === 'newTag') {
            const options = [];
            let lastCategory = null;

            fields.forEach(field => {
                if (field.category !== lastCategory) {
                    options.push({ value: field.category, label: field.category, divider: true, isDisabled: true });
                    lastCategory = field.category;
                }
                options.push({ value: field.name, label: field.display_name });
            });

            ret = (
                <div className={css.tagContainer}>
                    <NewTagInput
                        containerStyle={{
                            width: '100%',
                        }}
                        tags={fields
                            .filter(field => field.checked)
                            .map(field => ({
                                label: field.display_name,
                                value: field.name,
                            }))}
                        options={options}
                        isMulti
                        onChange={tags => {
                            // Remove fields that are checked, but not in tags
                            fields.forEach(field => {
                                if (field.checked && tags.find(tag => tag.value === field.name) === undefined) {
                                    onFieldClick(field);
                                }
                            });

                            // Select fields that are in tags, but not checked
                            tags.forEach(tag => {
                                const foundField = fields.find(field => field.name === tag.value);
                                if (!foundField.checked) {
                                    onFieldClick(foundField);
                                }
                            });
                        }}
                        {...group.data}
                    />
                </div>
            );
        }

        if (group.type === 'text') {
            ret = (
                <input
                    className={css.textType}
                    type="text"
                    value={group.value}
                    onChange={e => onFieldClick(e.target.value)}
                    disabled={group.disabled}
                    placeholder={group.placeholder || ''}
                />
            );
        }

        return ret;
    };

    const renderLabel = () => {
        const BasicLabel = (
            <div className={css.label}>
                {typeof label === 'string' ? (
                    <Translate id={label} /> || label
                ) : (
                    <Translate id={label.text} data={label.args} />
                )}
            </div>
        );

        if (labelTooltip) {
            return (
                <Tooltip
                    interactive
                    position="top-start"
                    title={translate(labelTooltip)}
                    wrapperClass={css.labelTooltip}
                >
                    {BasicLabel}
                    <Icon name="question-line" size={IconSizes.SMALL} />
                </Tooltip>
            );
        } else {
            return BasicLabel;
        }
    };

    const containerClass = labelPosition === 'top' ? css.containerLabelTop : css.container;
    const innerStyle = labelTooltip ? { cursor: 'pointer' } : {};
    const isUnifiedReport = reportType === reportTypes.unifiedReport;

    return (
        <React.Fragment>
            {group.skanPlus && (
                <div className={css.skanPlusBorderDivider}>
                    <div className={css.skanPlusContent}>
                        <group.skanPlus.icon />
                        <span className={css.skanPlusContentSpan}>
                            <Translate id={group.skanPlus.topBorderText} />
                        </span>
                    </div>
                </div>
            )}
            {group.topBorder && (
                <div className={css.borderDivider}>
                    {group.topBorderText && (
                        <span className={css.borderDividerSpan}>
                            <Translate id={group.topBorderText} />
                        </span>
                    )}
                </div>
            )}
            {fieldGroupWarning && (
                <div className={classNames(css.warningMessages, css.unifiedReportOutdatedMessage)}>
                    <WarningMessage
                        showSpacer={false}
                        showIcon={false}
                        type="whiteInfo"
                        message={fieldGroupWarning}
                        textReplace={fieldGroupWarningParameters}
                    />
                </div>
            )}
            <div
                className={classNames(containerClass, {
                    [css.topSectorContainer]: isUnifiedReport && group.topSector,
                    [css.fieldsGroupContainer]: isUnifiedReport && !group.topSector,
                })}
                style={{
                    ...(containerStyle || {}),
                    ...(group.data?.style || {}),
                }}
            >
                {group.topSector && (
                    <div className={classNames(css.topSectorHolder, css.labelContainer)}>
                        <group.topSector.numberIcon />
                        <div className={css.topSectorText}>{translate(group.topSector.text)}</div>
                        {group.topSector.tooltip && (
                            <Tooltip
                                position="top"
                                title={translate(group.topSector.tooltip)}
                                wrapperClass={css.labelTooltip}
                            >
                                <Icon name="question-line" size={IconSizes.SMALL} />
                            </Tooltip>
                        )}
                    </div>
                )}
                {label && (
                    <div
                        className={css.labelContainer}
                        style={{
                            ...labelStyle,
                            ...innerStyle,
                            fontSize: group.fontSize ? group.fontSize : '16px',
                        }}
                    >
                        {icon && <Icon name={icon} />}
                        {renderLabel()}
                        {group.is_new && (
                            <Tag
                                shouldTranslate
                                color="var(--pink100)"
                                value="STATIC.PAGES.REPORTS.NEW_TAG"
                                className={css.tag}
                            />
                        )}
                        {group.subText && (
                            <div className={css.groupSubText}>
                                <Translate id={group.subText} />
                            </div>
                        )}
                    </div>
                )}
                <div className={`${css.groupContainer} ${css[group.type]}`}>
                    {renderChildrenOnly ? children : getGroup()}
                    {bottomComponent}
                    {(showDownloadReport || fieldGroupError) && (
                        <div className={css.warningMessages}>
                            {fieldGroupError ? (
                                <WarningMessage
                                    type="error"
                                    message={fieldGroupError}
                                    textReplace={fieldGroupErrorParameters}
                                    showIcon={false}
                                />
                            ) : (
                                <WarningMessage
                                    message="STATIC.PAGES.REPORTS.DOWNLOAD_REPORT_MESSAGE"
                                    buttonText="STATIC.PAGES.REPORTS.DOWNLOAD_REPORT_BUTTON"
                                    buttonAction={onDownloadReport}
                                    type="info"
                                />
                            )}
                        </div>
                    )}
                    {warnings.length ? (
                        <div className={css.warningMessages}>
                            {warnings.map(warning => (
                                <WarningMessage key={warning.message} message={warning.message} textReplace={warning.data} />
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            {group.bottomBorder && <div className={css.borderDivider} />}
            {group.skanPlusBottomBorder && <div className={css.skanPlusBorderDivider} />}
        </React.Fragment>
    );
}

FieldGroup.propTypes = {
    fields: PropTypes.arrayOf(PropTypes.object),
    onFieldClick: PropTypes.func,
    addButton: PropTypes.objectOf(PropTypes.any),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]),
    group: PropTypes.objectOf(PropTypes.any),
    warnings: PropTypes.arrayOf(PropTypes.any),
    showDownloadReport: PropTypes.bool,
    labelPosition: PropTypes.string,
    labelTooltip: PropTypes.string,
    labelStyle: PropTypes.objectOf(PropTypes.any),
    containerStyle: PropTypes.objectOf(PropTypes.any),
    renderChildrenOnly: PropTypes.bool,
    reportType: PropTypes.string,
    style: PropTypes.objectOf(PropTypes.any),
    children: PropTypes.element,
    translate: PropTypes.func,
    selectedSources: PropTypes.arrayOf(PropTypes.any),
    dataTypeFields: PropTypes.arrayOf(PropTypes.any),
    fieldGroupWarning: PropTypes.string,
    fieldGroupError: PropTypes.string,
    fieldGroupWarningParameters: PropTypes.objectOf(PropTypes.any),
    fieldGroupErrorParameters: PropTypes.objectOf(PropTypes.any),
    onFieldsAvailabilityClick: PropTypes.func,
    isAdvanceMode: PropTypes.bool,
    onDownloadReport: PropTypes.func,
    showFieldsAvailabilityFeature: PropTypes.bool,
    bottomComponent: PropTypes.element,
    icon: PropTypes.string,
    dimensionsCollision: PropTypes.shape({
        firstGroupDimensions: PropTypes.arrayOf(PropTypes.string),
        secondGroupDimensions: PropTypes.arrayOf(PropTypes.string),
    }),
};

export default withLocalize(FieldGroup);
